/**
 * Utilidades para manejar sesiones persistentes de jugadores
 * Permite reconexión después de recargar la página o cerrar el navegador
 * MEJORADO: Ahora con soporte para reconexión más robusta y sesiones de larga duración
 */

import { logger } from './logger';

const SESSION_PREFIX = 'impostor_session_';
const SESSION_EXPIRY_MS = 4 * 60 * 60 * 1000; // 4 horas (extendido para mejor persistencia)
const AUTO_RENEWAL_THRESHOLD_MS = 30 * 60 * 1000; // Renovar si quedan menos de 30 minutos

export interface SessionData {
  sessionToken: string;
  roomCode: string;
  playerName: string;
  createdAt: number;
  expiresAt: number;
  lastActivity: number; // Nueva: rastrea la última actividad del usuario
  renewalCount: number; // Nueva: cuenta cuántas veces se ha renovado
}

/**
 * Genera un UUID v4 compatible con navegadores antiguos
 */
const generateUUID = (): string => {
  // Intentar usar crypto.randomUUID si está disponible
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // Fallback: generar UUID v4 manualmente
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Genera o recupera el token de sesión persistente para una sala
 * MEJORADO: Ahora con renovación automática si la sesión está cerca de expirar
 */
export const getOrCreateSessionToken = (roomCode: string, playerName: string): string => {
  const key = `${SESSION_PREFIX}${roomCode}`;
  const stored = localStorage.getItem(key);
  const now = Date.now();
  
  if (stored) {
    try {
      const data: SessionData = JSON.parse(stored);
      
      // Verificar si la sesión ha expirado completamente
      if (now >= data.expiresAt) {
        logger.log('Session expired, creating new token');
      } else if (data.playerName !== playerName) {
        logger.log('Player name mismatch, creating new token');
      } else {
        // Sesión válida - verificar si necesita renovación automática
        const timeUntilExpiry = data.expiresAt - now;
        
        if (timeUntilExpiry < AUTO_RENEWAL_THRESHOLD_MS) {
          // Renovar automáticamente si quedan menos de 30 minutos
          data.expiresAt = now + SESSION_EXPIRY_MS;
          data.lastActivity = now;
          data.renewalCount = (data.renewalCount || 0) + 1;
          localStorage.setItem(key, JSON.stringify(data));
          logger.log('Session auto-renewed, renewal count:', data.renewalCount);
        }
        
        logger.log('Using existing session token:', data.sessionToken, 
                   'expires in', Math.floor(timeUntilExpiry / 60000), 'minutes');
        return data.sessionToken;
      }
    } catch (e) {
      logger.error('Error parsing stored session:', e);
    }
  }
  
  // Crear nueva sesión
  const sessionToken = generateUUID();
  const sessionData: SessionData = {
    sessionToken,
    roomCode,
    playerName,
    createdAt: now,
    expiresAt: now + SESSION_EXPIRY_MS,
    lastActivity: now,
    renewalCount: 0
  };
  
  localStorage.setItem(key, JSON.stringify(sessionData));
  logger.log('Created new session token:', sessionToken, 'expires in', SESSION_EXPIRY_MS / 60000, 'minutes');
  
  return sessionToken;
};

/**
 * Obtiene el token de sesión existente (sin crear uno nuevo)
 * MEJORADO: Ahora con mejor validación y logging
 */
export const getExistingSessionToken = (roomCode: string): string | null => {
  const key = `${SESSION_PREFIX}${roomCode}`;
  const stored = localStorage.getItem(key);
  
  if (!stored) {
    logger.log('No existing session found for room:', roomCode);
    return null;
  }
  
  try {
    const data: SessionData = JSON.parse(stored);
    const now = Date.now();
    const timeUntilExpiry = data.expiresAt - now;
    
    // Verificar si ha expirado
    if (timeUntilExpiry <= 0) {
      logger.log('Session expired, removing from storage');
      localStorage.removeItem(key);
      return null;
    }
    
    // Renovar automáticamente si está cerca de expirar
    if (timeUntilExpiry < AUTO_RENEWAL_THRESHOLD_MS) {
      data.expiresAt = now + SESSION_EXPIRY_MS;
      data.lastActivity = now;
      data.renewalCount = (data.renewalCount || 0) + 1;
      localStorage.setItem(key, JSON.stringify(data));
      logger.log('Session auto-renewed on retrieval, renewal count:', data.renewalCount);
    }
    
    logger.log('Found existing session, expires in', Math.floor(timeUntilExpiry / 60000), 'minutes');
    return data.sessionToken;
  } catch (e) {
    logger.error('Error parsing stored session:', e);
    // Eliminar datos corruptos
    localStorage.removeItem(key);
    return null;
  }
};

/**
 * Elimina la sesión de una sala
 */
export const clearSession = (roomCode: string): void => {
  const key = `${SESSION_PREFIX}${roomCode}`;
  localStorage.removeItem(key);
  logger.log('Cleared session for room:', roomCode);
};

/**
 * Actualiza la expiración de la sesión (touch)
 * MEJORADO: Ahora actualiza también lastActivity y maneja errores mejor
 */
export const touchSession = (roomCode: string): boolean => {
  const key = `${SESSION_PREFIX}${roomCode}`;
  const stored = localStorage.getItem(key);
  
  if (!stored) {
    logger.warn('Cannot touch session: not found for room', roomCode);
    return false;
  }
  
  try {
    const data: SessionData = JSON.parse(stored);
    const now = Date.now();
    
    data.expiresAt = now + SESSION_EXPIRY_MS;
    data.lastActivity = now;
    localStorage.setItem(key, JSON.stringify(data));
    logger.log('Session touched for room:', roomCode);
    return true;
  } catch (e) {
    logger.error('Error updating session:', e);
    return false;
  }
};

/**
 * Limpia sesiones expiradas
 * MEJORADO: Ahora retorna el número de sesiones limpiadas
 */
export const cleanupExpiredSessions = (): number => {
  const now = Date.now();
  const keys = Object.keys(localStorage);
  let cleaned = 0;
  
  keys.forEach(key => {
    if (key.startsWith(SESSION_PREFIX)) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const data: SessionData = JSON.parse(stored);
          if (now >= data.expiresAt) {
            localStorage.removeItem(key);
            logger.log('Cleaned up expired session:', key);
            cleaned++;
          }
        }
      } catch (e) {
        // Eliminar datos corruptos
        localStorage.removeItem(key);
        logger.log('Removed corrupted session data:', key);
        cleaned++;
      }
    }
  });
  
  if (cleaned > 0) {
    logger.log(`Cleaned up ${cleaned} expired/corrupted session(s)`);
  }
  
  return cleaned;
};

/**
 * Obtiene información detallada de la sesión actual
 * NUEVA: Útil para debugging y monitoreo
 */
export const getSessionInfo = (roomCode: string): SessionData | null => {
  const key = `${SESSION_PREFIX}${roomCode}`;
  const stored = localStorage.getItem(key);
  
  if (!stored) return null;
  
  try {
    const data: SessionData = JSON.parse(stored);
    return data;
  } catch (e) {
    logger.error('Error parsing session info:', e);
    return null;
  }
};

/**
 * Verifica si hay una sesión válida sin modificarla
 * NUEVA: Útil para chequeos rápidos
 */
export const hasValidSession = (roomCode: string): boolean => {
  const key = `${SESSION_PREFIX}${roomCode}`;
  const stored = localStorage.getItem(key);
  
  if (!stored) return false;
  
  try {
    const data: SessionData = JSON.parse(stored);
    return Date.now() < data.expiresAt;
  } catch (e) {
    return false;
  }
};

// Limpiar sesiones expiradas al cargar
cleanupExpiredSessions();
